// ROI — Saves panel: the centralized Save Ledger (problems the system caught
// and made good). Reads GET /stats/roi/saves/summary + GET /stats/roi/saves.
//
// Verified saves drive the headline numbers; candidates/rejected are listed
// but never counted in the $ totals (that arithmetic lives server-side).

import { useCallback, useEffect, useState } from 'react';
import { statisticsService } from '../../services/statistics';
import type { RoiSave, RoiSaveSummary, TrackRecordRun, TrackRecordThread } from '../../services/statistics';
import { formatCurrencyFull } from '../../utils/formatting';
import styles from './StatisticsTab.module.css';

export const SAVE_KIND_LABELS: Record<string, string> = {
  certificate_notice_backfilled: 'Certificates delivered',
  order_verifier_correction: 'Sage correction (post-submit)',
  pre_ship_correction: 'Pre-ship correction',
  short_ship_prevented: 'Short-ship prevented',
  ghost_order_recovered: 'Lost order recovered',
  freight_recovered: 'Freight recovered',
  customer_email_misroute_prevented: 'Customer email misroute prevented',
  invoice_exception_presuggested: 'Invoice exception pre-suggested',
};

export function kindLabel(kind: string): string {
  return SAVE_KIND_LABELS[kind] ?? kind.replace(/_/g, ' ');
}

export const AUTOMATION_LABELS: Record<string, string> = {
  coa_notice_watchdog: 'Certificate notice check',
  order_extraction_accuracy_audit: 'Order-extraction accuracy audit',
  redflag_triage: 'RedFlag triage',
  nightly_health_check: 'Nightly health check',
  engagement_monitor: 'Portal engagement review',
  daily_update_digest: 'Daily update digest',
  daily_status_report: 'Daily status report',
  dry_review: 'DRY / systems review',
  kanban_recommendations: 'Kanban board audit',
};

export function automationLabel(key: string): string {
  return AUTOMATION_LABELS[key] ?? key.replace(/_/g, ' ');
}

function formatWindow(run: TrackRecordRun): string {
  if (run.window_start && run.window_end) return `${formatStamp(run.window_start)} → ${formatStamp(run.window_end)}`;
  if (run.window_end) return `to ${formatStamp(run.window_end)}`;
  return formatStamp(run.started_at);
}

function formatStamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function money(value: number | null | undefined): string {
  return value == null ? '—' : formatCurrencyFull(value);
}

export default function RoiSavesPanel() {
  const [summary, setSummary] = useState<RoiSaveSummary | null>(null);
  const [saves, setSaves] = useState<RoiSave[]>([]);
  const [total, setTotal] = useState(0);
  const [runs, setRuns] = useState<TrackRecordRun[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [threads, setThreads] = useState<TrackRecordThread[]>([]);
  const [threadsTotal, setThreadsTotal] = useState(0);
  const [kindFilter, setKindFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [summaryResult, listResult, runsResult, threadsResult] = await Promise.allSettled([
      statisticsService.getRoiSaveSummary(),
      statisticsService.getRoiSaves(kindFilter ? { saveKind: kindFilter } : undefined),
      statisticsService.getTrackRecordRuns(undefined, 25),
      statisticsService.getTrackRecordThreads(true, 50),
    ]);
    if (summaryResult.status === 'fulfilled') setSummary(summaryResult.value);
    if (listResult.status === 'fulfilled') {
      setSaves(listResult.value.saves);
      setTotal(listResult.value.total);
    }
    if (runsResult.status === 'fulfilled') {
      setRuns(runsResult.value.runs);
      setRunsTotal(runsResult.value.total);
    }
    if (threadsResult.status === 'fulfilled') {
      setThreads(threadsResult.value.threads);
      setThreadsTotal(threadsResult.value.total);
    }
    const failed = [summaryResult, listResult, runsResult, threadsResult].find((r) => r.status === 'rejected');
    if (failed && failed.status === 'rejected') {
      const reason: unknown = failed.reason;
      setError(reason instanceof Error ? reason.message : 'Failed to load the Save Ledger');
    }
    setLoading(false);
  }, [kindFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading && !summary) return <div className={styles.loading}>Loading the Save Ledger...</div>;

  const kinds = summary?.by_kind ?? [];

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {summary && (
        <div className={styles.kpiGrid} data-testid="roi-kpis">
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Verified saves</span>
            <span className={styles.kpiValue}>{summary.verified_saves.toLocaleString()}</span>
            <span className={styles.kpiChangeNeutral}>
              {summary.candidate_saves > 0 ? `+${summary.candidate_saves} awaiting verification` : 'all confirmed made good'}
            </span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>$ at risk protected</span>
            <span className={styles.kpiValue}>{formatCurrencyFull(summary.amount_at_risk_usd)}</span>
            <span className={styles.kpiChangeNeutral}>over-bills, over-ships, lost orders</span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Order value protected</span>
            <span className={styles.kpiValue}>{formatCurrencyFull(summary.order_value_protected_usd)}</span>
            <span className={styles.kpiChangeNeutral}>orders the saves sat on</span>
          </div>
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Last 30 days</span>
            <span className={styles.kpiValue}>{summary.saves_last_30_days.toLocaleString()}</span>
            <span className={styles.kpiChangeNeutral}>latest: {formatStamp(summary.latest_made_good_at)}</span>
          </div>
        </div>
      )}

      {kinds.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>By kind of catch</h3>
          <table className={styles.dataTable} data-testid="roi-by-kind">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Saves</th>
                <th>$ at risk</th>
                <th>Order value</th>
              </tr>
            </thead>
            <tbody>
              {kinds.map((k) => (
                <tr key={k.save_kind}>
                  <td>{kindLabel(k.save_kind)}</td>
                  <td>{k.count}</td>
                  <td>{formatCurrencyFull(k.amount_at_risk_usd)}</td>
                  <td>{formatCurrencyFull(k.order_value_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.filterBar}>
          <h3 className={styles.sectionTitle}>
            The ledger{total ? ` · ${total.toLocaleString()} ${total === 1 ? 'save' : 'saves'}` : ''}
          </h3>
          <label>
            Kind{' '}
            <select
              aria-label="Filter by kind"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
            >
              <option value="">All kinds</option>
              {Object.entries(SAVE_KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {saves.length === 0 ? (
          <div className={styles.filterNotice}>No saves recorded{kindFilter ? ' for this kind' : ''} yet.</div>
        ) : (
          <table className={styles.dataTable} data-testid="roi-ledger">
            <thead>
              <tr>
                <th>Made good</th>
                <th>Order</th>
                <th>Customer</th>
                <th>Kind</th>
                <th>What happened</th>
                <th>$ at risk</th>
                <th>Verified by</th>
              </tr>
            </thead>
            <tbody>
              {saves.map((s) => (
                <tr key={s.id} data-testid={`roi-save-${s.sage_order_no ?? s.id}`}>
                  <td>{formatStamp(s.made_good_at ?? s.detected_at)}</td>
                  <td>{s.sage_order_no ?? '—'}</td>
                  <td>{s.customer_name ?? s.customer_no ?? '—'}</td>
                  <td>{kindLabel(s.save_kind)}</td>
                  <td>
                    {s.detail ? (
                      <details>
                        <summary>{s.headline}</summary>
                        <p>{s.detail}</p>
                      </details>
                    ) : (
                      s.headline
                    )}
                  </td>
                  <td>{money(s.amount_at_risk_usd)}</td>
                  <td>
                    {s.status === 'verified' ? s.adjudicated_by : `${s.status} · ${s.adjudicated_by}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Open threads{threadsTotal ? ` · ${threadsTotal.toLocaleString()} ${threadsTotal === 1 ? 'thread' : 'threads'} still in motion` : ''}
        </h3>
        {threads.length === 0 ? (
          <div className={styles.filterNotice}>No open threads — every lifecycle on record is resolved or benign.</div>
        ) : (
          <table className={styles.dataTable} data-testid="roi-threads">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Since</th>
                <th>Last activity</th>
                <th>What's pending</th>
                <th>Events</th>
              </tr>
            </thead>
            <tbody>
              {threads.map((t) => (
                <tr key={t.id} data-testid={`roi-thread-${t.subject_ref}-${t.thread_kind}`}>
                  <td>{t.subject_ref}</td>
                  <td>{t.thread_kind.replace(/_/g, ' ')}</td>
                  <td>{t.status.replace(/_/g, ' ')}</td>
                  <td>{formatStamp(t.opened_at)}</td>
                  <td>{formatStamp(t.last_event_at)}</td>
                  <td>{t.pending_action ?? t.title}</td>
                  <td>{t.event_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Automation track record{runsTotal ? ` · ${runsTotal.toLocaleString()} ${runsTotal === 1 ? 'run' : 'runs'}` : ''}
        </h3>
        {runs.length === 0 ? (
          <div className={styles.filterNotice}>No automation runs recorded yet.</div>
        ) : (
          <table className={styles.dataTable} data-testid="roi-runs">
            <thead>
              <tr>
                <th>Automation</th>
                <th>Window</th>
                <th>Status</th>
                <th>Examined</th>
                <th>Found</th>
                <th>Actioned</th>
                <th>What happened</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} data-testid={`roi-run-${r.id}`}>
                  <td>{automationLabel(r.automation_key)}</td>
                  <td>{formatWindow(r)}</td>
                  <td>{r.status}</td>
                  <td>{r.examined_count.toLocaleString()}</td>
                  <td>{r.found_count.toLocaleString()}{r.findings_count ? ` (${r.findings_count} logged)` : ''}</td>
                  <td>{r.actioned_count.toLocaleString()}</td>
                  <td>{r.summary ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
